import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ArrowLeft, Clock, Calendar, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WritingPage() {
    const { id } = useParams();
    const [writing, setWriting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [readProgress, setReadProgress] = useState(0);
    const contentRef = useRef(null);

    const fetchWriting = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/writings/${id}`);
            setWriting(res.data);
        } catch (err) {
            setError('Writing not found');
        }
        setLoading(false);
    }, [id]);

    useEffect(() => {
        fetchWriting();
    }, [fetchWriting]);

    // Track reading progress
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
            setReadProgress(Math.min(100, Math.max(0, progress)));
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Strip HTML for meta description
    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').slice(0, 160);
    };

    // Process content to fix spacing issues
    const processContent = (html) => {
        if (!html) return '';
        
        // If content doesn't have paragraph tags, wrap text in paragraphs
        let processed = html;
        
        // Check if content already has proper HTML structure
        const hasProperStructure = /<p[^>]*>|<div[^>]*>|<br\s*\/?>/i.test(html);
        
        if (!hasProperStructure) {
            // Split by double newlines and wrap each block in <p> tags
            processed = html
                .split(/\n\n+/)
                .map(para => para.trim())
                .filter(para => para.length > 0)
                .map(para => `<p>${para}</p>`)
                .join('\n');
        }
        
        // Fix common spacing issues
        processed = processed
            // Fix missing spaces after periods followed by capital letters
            .replace(/\.([A-Z])/g, '. $1')
            // Fix missing spaces after commas
            .replace(/,([A-Za-z])/g, ', $1')
            // Fix missing spaces after colons
            .replace(/:([A-Za-z])/g, ': $1')
            // Fix multiple spaces
            .replace(/\s{2,}/g, ' ')
            // Convert single newlines within text to proper spacing
            .replace(/([^>])\n([^<])/g, '$1 $2');
        
        return processed;
    };

    // Render content based on type
    const renderContent = () => {
        if (!writing) return null;

        // If it's a Substack link, show link to external
        if (writing.content_type === 'substack' && writing.substack_url) {
            return (
                <div className="text-center py-12">
                    <p className="text-muted-foreground mb-6">
                        This article is hosted on Substack.
                    </p>
                    <a
                        href={writing.substack_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm uppercase tracking-widest border-2 border-primary"
                        style={{ fontFamily: "'Silkscreen', cursive" }}
                    >
                        Read on Substack
                        <ExternalLink className="h-4 w-4" />
                    </a>
                </div>
            );
        }

        // For direct content or doc content
        const content = writing.content_type === 'doc' ? writing.doc_content : writing.content;
        
        // Sanitize and process content
        const sanitizedContent = content ? DOMPurify.sanitize(processContent(content)) : '';
        
        return (
            <div 
                className="writing-content"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
                {sanitizedContent ? (
                    <div 
                        className="writing-body"
                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                    />
                ) : (
                    <p className="text-muted-foreground italic">
                        No content available for this writing.
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation />
                <main className="py-16 sm:py-24">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="animate-pulse">
                            <div className="h-8 w-3/4 bg-muted mb-4" />
                            <div className="h-4 w-1/4 bg-muted mb-8" />
                            <div className="space-y-3">
                                <div className="h-4 w-full bg-muted" />
                                <div className="h-4 w-full bg-muted" />
                                <div className="h-4 w-2/3 bg-muted" />
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error || !writing) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation />
                <main className="py-16 sm:py-24">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 
                            className="text-2xl sm:text-3xl mb-4"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                        >
                            Writing Not Found
                        </h1>
                        <p className="text-muted-foreground mb-8">
                            The writing you're looking for doesn't exist or has been removed.
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-primary hover:underline"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Link>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="writing-page">
            <Helmet>
                <title>{writing?.title ? `${writing.title} | Shreyan Sahukar` : 'Writing | Shreyan Sahukar'}</title>
                {writing?.excerpt && <meta name="description" content={writing.excerpt} />}
                {writing?.title && <meta property="og:title" content={`${writing.title} | Shreyan Sahukar`} />}
                {writing?.excerpt && <meta property="og:description" content={writing.excerpt} />}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://shreyansahukar.com/writing/${id}`} />
                {writing?.image_url && <meta property="og:image" content={writing.image_url} />}
                <meta name="twitter:card" content="summary_large_image" />
                {writing?.title && <meta name="twitter:title" content={writing.title} />}
                {writing?.excerpt && <meta name="twitter:description" content={writing.excerpt} />}
                <link rel="canonical" href={`https://shreyansahukar.com/writing/${id}`} />
            </Helmet>
            {writing && (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Article",
                            "headline": writing.title || "",
                            "description": writing.excerpt || "",
                            "author": {
                                "@type": "Person",
                                "name": "Shreyan Sahukar"
                            },
                            "datePublished": writing.created_at || "",
                            "dateModified": writing.updated_at || ""
                        })}
                    </script>
                </Helmet>
            )}

            {/* Reading Progress Bar - Fixed at top */}
            <div className="fixed top-0 left-0 right-0 z-[60] h-1.5 bg-muted/50">
                <div 
                    className="h-full bg-primary transition-all duration-150 ease-out"
                    style={{ width: `${readProgress}%` }}
                    data-testid="reading-progress"
                />
            </div>

            <Navigation />

            {/* Sticky Back Button - Fixed on left side */}
            <Link
                to="/#writings"
                className="fixed left-4 top-24 z-40 hidden lg:flex items-center gap-2 px-3 py-2 bg-background border-2 border-border hover:border-primary hover:text-primary transition-all text-sm uppercase tracking-widest group"
                style={{ fontFamily: "'Silkscreen', cursive" }}
                data-testid="sticky-back-btn"
            >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="hidden xl:inline">Back</span>
            </Link>
            
            <main className="py-16 sm:py-24">
                {/* Wider container - max-w-4xl instead of max-w-3xl */}
                <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" ref={contentRef}>
                    {/* Mobile back link */}
                    <Link
                        to="/#writings"
                        className="lg:hidden inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8 text-sm uppercase tracking-widest"
                        data-testid="back-to-writings"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Writings
                    </Link>

                    {/* Header */}
                    <header className="mb-12">
                        <h1 
                            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase mb-4"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                            data-testid="writing-title"
                        >
                            {writing.title}
                        </h1>
                        
                        {writing.excerpt && (
                            <p className="text-lg text-muted-foreground mb-6">
                                {writing.excerpt}
                            </p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-t-2 border-b-2 border-dashed border-border py-4">
                            <span className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                {formatDate(writing.created_at)}
                            </span>
                            <span className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {writing.read_time}
                            </span>
                        </div>

                        {/* Featured Image */}
                        {writing.image_url && (
                            <div className="mt-8 border-2 border-border overflow-hidden">
                                <img 
                                    src={writing.image_url} 
                                    alt={writing.title}
                                    className="w-full h-64 sm:h-80 object-cover"
                                />
                            </div>
                        )}
                    </header>

                    {/* Content */}
                    <div className="retro-card p-6 sm:p-10" data-testid="writing-content">
                        {renderContent()}
                    </div>

                    {/* Footer navigation */}
                    <div className="mt-12 pt-8 border-t-2 border-dashed border-border">
                        <Link
                            to="/#writings"
                            className="inline-flex items-center gap-2 text-primary hover:underline text-sm uppercase tracking-widest"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            View All Writings
                        </Link>
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}
